/**
 * The camera, for both places that shoot.
 *
 * At the bench it takes the goods being received — the neutral ones above all,
 * which have no configured view — so that one day the product can be
 * recognised from those photos (rule 38-bis). In production it takes the
 * signature of completion (rule 20). It moved out of `screens/receiving/` when
 * the second caller arrived: two screens, one camera, and a component living
 * inside one feature would have made the other import that feature's insides.
 *
 * There is no recognition here at all: it acquires, and that is all.
 *
 * ## Le tre decisioni
 *
 * **Le tracce si fermano allo smontaggio.** Una spia webcam accesa su un banco
 * condiviso è il guasto che si vede da lontano e che nessuno segnala.
 *
 * **Lo scatto si guarda prima di mandarlo.** «Rifai» / «Usa questa» fanno due
 * cose insieme: tengono fuori dall'archivio gli scatti storti — un dataset è
 * fatto di quello che ci entra — e sono la sola mitigazione privacy che
 * l'operatrice possa verificare, perché vede cosa sta per partire. In cabina
 * luce si fotografa merce, ma una mano o un cartellino col nome di un cliente
 * ci entrano.
 *
 * **Non blocca mai — e chi decide è chi chiama.** Ogni stato che non sia
 * «pronta» offre una via d'uscita, che è `onChiudi`. Al banco quella via si
 * chiama «Continua senza foto», perché la foto si chiede e non si pretende;
 * in produzione è «Annulla», perché la regola 20 la pretende davvero. Il
 * componente non conosce la differenza: apre una porta, e cosa c'è dietro lo
 * sa lo schermo che l'ha aperta.
 *
 * ## Il formato, e perché è uno solo
 *
 * JPEG, quadrato, lato al massimo 1280. Il quadrato perché la cabina luce è
 * quadrata e perché chi addestra vuole immagini della stessa forma; un formato
 * solo perché significa un percorso di decodifica solo, il giorno che quelle
 * immagini serviranno davvero.
 */

import { useEffect, useRef, useState } from 'react';

/** Il lato dell'immagine che parte. Vedi la nota sul formato, qui sopra. */
const LATO = 1280;
/** 0,82 tiene un capo in cabina luce fra i 150 e i 300 KB. */
const QUALITA = 0.82;

/**
 * I cinque stati delle linee guida, detti con le parole di questo schermo.
 *
 * `insicura` è quello che sorprende chi prova sul portatile e non in reparto:
 * `navigator.mediaDevices` **non esiste** fuori da un secure context, quindi
 * una postazione raggiunta in `http://` su un IP di LAN non ha fotocamera. Va
 * detto con la parola giusta — serve https — invece di mostrare un rettangolo
 * nero che sembra un guasto della macchina.
 */
type Stato =
  | { readonly q: 'chiedo' }
  | { readonly q: 'pronta' }
  | { readonly q: 'negata' }
  | { readonly q: 'assente' }
  | { readonly q: 'insicura' }
  | { readonly q: 'rotta'; readonly motivo: string };

const FRASI: Readonly<Record<Exclude<Stato['q'], 'pronta' | 'rotta'>, string>> = {
  chiedo: 'Accendo la fotocamera…',
  negata: 'Il permesso per la fotocamera è stato negato. Concedilo dalle impostazioni del browser.',
  assente: 'Nessuna fotocamera collegata a questa postazione.',
  insicura:
    'La fotocamera funziona solo su una pagina https. Questa postazione è aperta in http: ' +
    'chiedi che venga raggiunta con https.',
};

export interface PropsCamera {
  /** I byte dello scatto, quando l'operatrice ha detto «usa questa». */
  readonly onScatto: (immagine: Blob) => void;
  /** Chiudere senza mandare niente: «continua senza foto», o il gesto annullato. */
  readonly onChiudi: () => void;
  /** Che cosa si sta fotografando, scritto sopra l'immagine. */
  readonly soggetto?: string;
  /** Un invio già in corso: si disabilita per non mandarne due. */
  readonly inCorso?: boolean;
  /**
   * Come si chiama la via d'uscita, con le parole di chi ha aperto.
   *
   * Al banco «Continua senza foto», in produzione «Annulla»: sono due frasi
   * diverse perché sono due conseguenze diverse, e il componente non deve
   * conoscerle. Predefinita quella del banco, che è la prima ad essere
   * esistita.
   */
  readonly uscita?: string;
}

export function Camera({ onScatto, onChiudi, soggetto, inCorso, uscita }: PropsCamera) {
  const [stato, setStato] = useState<Stato>({ q: 'chiedo' });
  /** Lo scatto in attesa di conferma, e la sua anteprima. */
  const [preso, setPreso] = useState<{ blob: Blob; url: string } | null>(null);

  const video = useRef<HTMLVideoElement>(null);
  const flusso = useRef<MediaStream | null>(null);

  useEffect(() => {
    let vivo = true;

    async function accendi(): Promise<void> {
      // `mediaDevices` assente **non è un guasto**: è la pagina che non sta in
      // un secure context. Distinguerlo è la differenza fra una frase che dice
      // cosa fare e un rettangolo nero.
      if (typeof navigator === 'undefined' || navigator.mediaDevices === undefined) {
        setStato({ q: 'insicura' });
        return;
      }

      try {
        const preso = await navigator.mediaDevices.getUserMedia({
          video: {
            // La camera posteriore su un tablet: è quella rivolta al bancone.
            facingMode: 'environment',
            width: { ideal: LATO },
            height: { ideal: LATO },
          },
          audio: false,
        });

        // Smontata mentre la si accendeva: si spegne subito, o resta accesa
        // senza che nessuno la stia guardando.
        if (!vivo) {
          preso.getTracks().forEach((t) => t.stop());
          return;
        }

        // Qui si tiene il flusso e basta: appenderlo al `<video>` adesso non
        // funzionerebbe, perché lo stato è ancora «chiedo» e l'elemento non è
        // montato. Lo fa l'effetto qui sotto, quando la ripresa esiste davvero.
        flusso.current = preso;
        setStato({ q: 'pronta' });
      } catch (e) {
        if (!vivo) return;
        const nome = e instanceof Error ? e.name : '';
        if (nome === 'NotAllowedError' || nome === 'SecurityError') setStato({ q: 'negata' });
        else if (nome === 'NotFoundError' || nome === 'OverconstrainedError') {
          setStato({ q: 'assente' });
        } else setStato({ q: 'rotta', motivo: e instanceof Error ? e.message : String(e) });
      }
    }

    void accendi();

    return () => {
      vivo = false;
      // Il punto di tutto l'effetto: qui la spia si spegne.
      flusso.current?.getTracks().forEach((t) => t.stop());
      flusso.current = null;
    };
  }, []);

  /**
   * Il flusso si appende quando la ripresa **c'è**.
   *
   * Il `<video>` vive dentro il ramo «pronta», e prima di quel render il ref è
   * `null`: assegnare `srcObject` dentro la promessa di `getUserMedia` cadeva
   * nel vuoto e lasciava in cabina un rettangolo grigio, con la spia della
   * webcam accesa — il guasto sembrava della macchina e invece era dell'ordine
   * delle cose. Legarlo allo stato mette l'assegnazione **dopo** il montaggio,
   * che è l'unico momento in cui può attaccarsi a qualcosa.
   */
  useEffect(() => {
    if (stato.q !== 'pronta') return;
    const ripresa = video.current;
    if (ripresa === null || flusso.current === null) return;
    ripresa.srcObject = flusso.current;
    // `autoPlay` basta quasi sempre; il `play()` esplicito copre il browser che
    // considera la sorgente cambiata a montaggio avvenuto.
    void ripresa.play();
  }, [stato.q]);

  /** L'anteprima presa si revoca quando cambia: un blob per volta, non uno per scatto. */
  useEffect(() => {
    const url = preso?.url;
    return () => {
      if (url !== undefined) URL.revokeObjectURL(url);
    };
  }, [preso]);

  function scatta(): void {
    const sorgente = video.current;
    const tela = document.createElement('canvas');
    // Ritaglio quadrato sul lato minore: la cabina luce è quadrata, e un
    // classificatore vuole immagini della stessa forma.
    const largo = sorgente?.videoWidth ?? LATO;
    const alto = sorgente?.videoHeight ?? LATO;
    const lato = Math.min(largo, alto) || LATO;
    tela.width = Math.min(lato, LATO);
    tela.height = tela.width;

    const pennello = tela.getContext('2d');
    if (pennello !== null && sorgente !== null) {
      pennello.drawImage(
        sorgente,
        (largo - lato) / 2,
        (alto - lato) / 2,
        lato,
        lato,
        0,
        0,
        tela.width,
        tela.height,
      );
    }

    tela.toBlob(
      (blob) => {
        if (blob === null) return;
        setPreso({ blob, url: URL.createObjectURL(blob) });
      },
      'image/jpeg',
      QUALITA,
    );
  }

  if (stato.q !== 'pronta') {
    const dice = stato.q === 'rotta' ? `La fotocamera non parte: ${stato.motivo}` : FRASI[stato.q];
    return (
      <div className="fotocamera">
        <p className="stato" data-stato={quale(stato.q)}>
          {dice}
        </p>
        <div className="mods">
          <button className="mod" onClick={onChiudi}>
            {uscita ?? 'Continua senza foto'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fotocamera">
      {soggetto !== undefined && <b className="gesto__k">{soggetto}</b>}

      <div className="fotocamera__stage">
        {/* L'anteprima copre la ripresa invece di sostituirla: tornare a
            «rifai» non deve rimettere in piedi il flusso da capo. */}
        <video ref={video} className="fotocamera__ripresa" autoPlay playsInline muted />
        {preso !== null && (
          <img className="fotocamera__scatto" src={preso.url} alt="Lo scatto, prima di mandarlo" />
        )}
      </div>

      <div className="mods">
        {preso === null ? (
          <>
            <button className="mod mod--primario" onClick={scatta} disabled={inCorso === true}>
              Scatta
            </button>
            <button className="mod" onClick={onChiudi}>
              {uscita ?? 'Continua senza foto'}
            </button>
          </>
        ) : (
          <>
            <button className="mod" onClick={() => setPreso(null)} disabled={inCorso === true}>
              Rifai
            </button>
            <button
              className="mod mod--primario"
              onClick={() => onScatto(preso.blob)}
              disabled={inCorso === true}
            >
              Usa questa
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** I nomi dei cinque stati delle linee guida, per chi legge il DOM nei test. */
function quale(q: Exclude<Stato['q'], 'pronta'>): string {
  if (q === 'chiedo') return 'caricando';
  if (q === 'negata') return 'non-permesso';
  if (q === 'assente') return 'vuoto';
  return 'rotto';
}
